/**
 * Shared report filter ↔ ConditionRow wiring (builder, viewer, dashboard).
 */
import {
  FILTER_VALUE_TYPE_STATIC,
  FILTER_VALUE_TYPE_FIELD,
  getFilterValueType,
  parseReportFilterValueFields,
} from './filterFieldValueUtils';
import { fromConditionRowOperator, toConditionRowOperator } from './reportFilterOperators';

/** ConditionRow options for report filters (field_config_id compare targets). */
export const REPORT_FILTER_CONDITION_ROW_OPTIONS = {
  fieldKey: 'field',
  nameKey: 'id',
  labelKey: 'field_display_name',
  hideSubtitle: true,
  fieldColSize: 3,
  operatorColSize: 2,
  valueColSize: 7,
  useSearchableDropdown: true,
  allowFieldValue: true,
  fieldCompareStoreKey: 'id',
};

/** Runtime modal: field fixed, value may switch static vs field. */
export const REPORT_RUNTIME_FILTER_CONDITION_ROW_OPTIONS = {
  ...REPORT_FILTER_CONDITION_ROW_OPTIONS,
  fieldColSize: 4,
  operatorColSize: 3,
  valueColSize: 5,
  fieldReadOnly: true,
  labelKey: 'field_label',
};

export function buildReportFilterConditionState(filter, availableFields = []) {
  const selectedField =
    availableFields.find(
      (f) => String(f.id) === String(filter.field_config_id)
    ) || null;
  const rowValueType = getFilterValueType(filter);
  const parsed = parseReportFilterValueFields(
    filter.value,
    filter.valueType ?? rowValueType,
    {
      dataType: selectedField?.data_type || filter.data_type,
      operator: filter.operator,
    }
  );
  return {
    field: filter.field_config_id ? String(filter.field_config_id) : '',
    operator: toConditionRowOperator(filter.operator || 'equals'),
    valueType: parsed.valueType ?? rowValueType,
    value: parsed.value,
  };
}

/**
 * Apply ConditionRow onChange to a report filter draft row (immutable updater).
 * @param {Array} prev - current draft array
 * @param {number} filterIndex
 * @param {string} key
 * @param {*} val
 * @param {Array} availableFields
 */
export function applyReportFilterConditionChange(
  prev,
  filterIndex,
  key,
  val,
  availableFields = []
) {
  const row = prev[filterIndex];
  if (!row) return prev;

  if (key === 'field') {
    if (val === '' || val == null) {
      return prev.map((f, i) =>
        i === filterIndex
          ? {
              ...f,
              field_config_id: '',
              field_name: '',
              field_label: '',
              object_name: '',
              object_label: '',
              data_type: '',
              operator: 'equals',
              value: '',
              valueType: FILTER_VALUE_TYPE_STATIC,
            }
          : f
      );
    }
    const selectedField = availableFields.find(
      (f) =>
        String(f.id) === String(val) ||
        String(f.field_config_id || '') === String(val) ||
        String(f.field_name || '') === String(val)
    );
    if (!selectedField) return prev;
    const configId = selectedField.id ?? selectedField.field_config_id;
    return prev.map((f, i) =>
      i === filterIndex
        ? {
            ...f,
            field_config_id: configId,
            field_name: selectedField.field_name,
            field_label: selectedField.field_label,
            object_name: selectedField.object_name,
            object_label: selectedField.object_label,
            data_type: selectedField.data_type,
            operator: 'equals',
            value: '',
            valueType: FILTER_VALUE_TYPE_STATIC,
          }
        : f
    );
  }

  if (key === 'valueType') {
    return prev.map((f, i) =>
      i === filterIndex ? { ...f, valueType: val, value: '' } : f
    );
  }

  if (key === 'operator') {
    return prev.map((f, i) =>
      i === filterIndex
        ? { ...f, operator: fromConditionRowOperator(val) }
        : f
    );
  }

  if (key === 'value') {
    const valueType = row.valueType || FILTER_VALUE_TYPE_STATIC;
    return prev.map((f, i) =>
      i === filterIndex ? { ...f, value: val, valueType } : f
    );
  }

  if (key === 'displayValue') {
    return prev;
  }

  return prev;
}

/** Factory for ConditionRow onChange handlers. */
export function createReportFilterConditionChangeHandler(
  setDraft,
  availableFields = []
) {
  return (filterIndex) => (idx, key, val) => {
    if (idx !== filterIndex) return;
    setDraft((prev) =>
      applyReportFilterConditionChange(
        prev,
        filterIndex,
        key,
        val,
        availableFields
      )
    );
  };
}

/** Patch multiple keys in one update (value type toggles). */
export function createReportFilterConditionPatchHandler(
  setDraft,
  availableFields = []
) {
  return (filterIndex) => (idx, patch) => {
    if (idx !== filterIndex || !patch || typeof patch !== 'object') return;
    setDraft((prev) => {
      let next = [...prev];
      Object.entries(patch).forEach(([k, v]) => {
        next = applyReportFilterConditionChange(
          next,
          filterIndex,
          k,
          v,
          availableFields
        );
      });
      return next;
    });
  };
}
